import { CityInformation } from './../../classes/city-information';
import { WeatherServiceYrno } from './../../Services/weather.service_yrno';
import { LocalStorageService } from './../../Services/local-storage.service';
import { ServerCacheService } from './../../Services/server-cache.service';
import { WeatherData } from './../../classes/weather-data';
import { WeatherService } from './../../Services/weather.service';
import { Weather, Forecast } from './../../classes/weather';
import { Component, OnInit, Input, OnDestroy, SimpleChanges, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, interval, Subscription, forkJoin } from 'rxjs';
import { take, switchMap } from 'rxjs/operators';
import { EventEmitterService } from 'src/app/event-emitter.service';
import { LocalStorageInformation } from 'src/app/app.component';


@Component({
  selector: 'app-weather',
  templateUrl: './weather.component.html',
  styleUrls: ['./weather.component.css'],

})
export class WeatherComponent implements OnInit, OnDestroy {
  @Input() city: string;
  @Input() cityID: string;
  @Input() yrnoLocation: string;
  @Input() currentCity: string;
  weatherForecastSchema: any;


  private updateSubscription: Subscription;
  data: WeatherData;


  constructor(private eventEmitterService: EventEmitterService,
              private weatherService: WeatherService,
              private weatherServiceYrno: WeatherServiceYrno,
              private localStorageService: LocalStorageService,
              private serverCacheService: ServerCacheService,
              @Inject(PLATFORM_ID) private platformId: any) { }

  ngOnInit() {


    this.data = new WeatherData();
    this.data.city = this.city;
    this.data.cityID = this.cityID;



    // this.updateWeather();

    const storedCity = this.localStorageService.getStoredSelectedCity();

    // only start periodic updates in browser
    if (isPlatformBrowser(this.platformId)) {
      // 21 minutes == 1260000
      // 15 min = 900000
      // 10 min == 600000
      // 6 min =        360000
      this.updateSubscription = interval(600000).subscribe((val) => {
        if (!storedCity || !storedCity.selectedCityName || storedCity.selectedCityName === this.city) {
          this.updateWeather();
        }
      });
    }

    if (!storedCity || !storedCity.selectedCityName || storedCity.selectedCityName === this.city) {
      this.updateWeather();
    }

  }
  updateWeatherEvent(city: string) {
    if (city === this.city)
    {
      this.updateWeather();
    }
  }

  ngOnDestroy() {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    // Add '${implements OnChanges}' to the class.

    if (changes.currentCity.currentValue == this.city)
    {
      this.updateWeather();
    }

  }

  updateWeather() {
    // Only update weather if we're in the browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Check server cache for current weather data
    this.serverCacheService.getStoredCurrentWeatherInfoFromServer(this.city).pipe(
      take(1),
      switchMap(weatherData => {
        // Defensive normalization: ensure nested structures exist to avoid TypeErrors
        try {
          if (weatherData && !weatherData.weather) {
            (weatherData as any).weather = {};
          }
          if (weatherData && weatherData.weather && typeof (weatherData as any).weather.weather === 'undefined') {
            // If nested current-weather is missing, set an empty object to avoid property access errors
            (weatherData as any).weather.weather = {};
          }
        } catch (e) {
          // swallow normalization errors
        }
        let diffMins;
        if (weatherData && weatherData.weather && weatherData.weather.refreshtime) {
          const currentDate = new Date();
          const diffMs = (currentDate.getTime() - new Date(weatherData.weather.refreshtime).getTime());
          diffMins = Math.round(diffMs / 60000);
        }

        const DIFF_IN_MINUTES_WHEN_UPDATE_IS_MADE = 30;
        
        if (!weatherData || diffMins === undefined || diffMins >= DIFF_IN_MINUTES_WHEN_UPDATE_IS_MADE) {
          // Data is stale or doesn't exist, fetch new data
          return this.weatherService.getCurrentTemperature$(this.city, this.cityID).pipe(
            take(1),
            switchMap(currentWeatherData => {
              const localData = new LocalStorageInformation();
              localData.weather = new WeatherData();
              localData.weather.city = this.city;
              localData.weather.cityID = this.cityID;
              localData.weather.weather = currentWeatherData;
              localData.weather.refreshtime = new Date();

              // Store to server cache
              this.serverCacheService.storeCityCurrentWeatherToServer(localData).pipe(take(1)).subscribe();

              this.data.weather = currentWeatherData;
              this.data.refreshtime = new Date();
              this.eventEmitterService.onWeatherChanged(currentWeatherData.Temperature, this.city);

              return new Promise(resolve => resolve(localData));
            })
          );
        } else {
          // Use cached data if it contains the current weather
          const cachedCurrent = weatherData?.weather?.weather;
          if (cachedCurrent) {
            if (cachedCurrent.Temperature) {
              this.eventEmitterService.onWeatherChanged(cachedCurrent.Temperature, this.city);
            }
            this.data.weather = cachedCurrent;
            if (weatherData.weather && weatherData.weather.refreshtime) {
              this.data.refreshtime = new Date(weatherData.weather.refreshtime);
            }
            return new Promise(resolve => resolve(weatherData));
          }

          // Cached entry exists but missing the nested current weather object — fetch fresh current weather
          return this.weatherService.getCurrentTemperature$(this.city, this.cityID).pipe(
            take(1),
            switchMap(currentWeatherData => {
              const localData = new LocalStorageInformation();
              localData.weather = new WeatherData();
              localData.weather.city = this.city;
              localData.weather.cityID = this.cityID;
              localData.weather.weather = currentWeatherData;
              localData.weather.refreshtime = new Date();

              // Store to server cache
              this.serverCacheService.storeCityCurrentWeatherToServer(localData).pipe(take(1)).subscribe();

              this.data.weather = currentWeatherData;
              this.data.refreshtime = new Date();
              this.eventEmitterService.onWeatherChanged(currentWeatherData.Temperature, this.city);

              return new Promise(resolve => resolve(localData));
            })
          );
        }
      })
    ).subscribe({
      next: () => {
        // Current weather handled, now handle forecast
        this.updateForecastData();
      },
      error: err => {
        console.error('Error updating current weather:', err);
      }
    });
  }

  private updateForecastData() {
    // Only update forecast if we're in the browser
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    this.serverCacheService.getStoredForecastWeatherInfoFromServer(this.city).pipe(
      take(1),
      switchMap(localForecast => {
        let diffMins;
        if (localForecast && localForecast.weather && localForecast.weather.refreshtime) {
          const currentDate = new Date();
          const diffMs = (currentDate.getTime() - new Date(localForecast.weather.refreshtime).getTime());
          diffMins = Math.round(diffMs / 60000);
        }

        const DIFF_IN_MINUTES_WHEN_UPDATE_IS_MADE = 30;

        if (!localForecast || diffMins === undefined || diffMins >= DIFF_IN_MINUTES_WHEN_UPDATE_IS_MADE) {
          // Fetch fresh forecast data
          const localData = new LocalStorageInformation();
          localData.weather = new WeatherData();
          localData.weather.city = this.city;
          localData.weather.cityID = this.cityID;

          return this.weatherService.getForecast$(this.city).pipe(
            take(1),
            switchMap(forecastData => {
              localData.weather.forecast = forecastData;
              localData.weather.refreshtime = new Date();
              this.data.forecast = forecastData;
              this.data.refreshtime = new Date();

              if (isPlatformBrowser(this.platformId)) {
                return this.weatherServiceYrno.getForecastYrno$(this.city, this.yrnoLocation).pipe(
                  take(1),
                  switchMap(yrnoData => {
                    localData.weather.forecastYrno = yrnoData;
                    this.data.forecastYrno = yrnoData;

                    // Store combined forecast data to server cache
                    this.serverCacheService.storeCityForecastWeatherToServer(localData).pipe(take(1)).subscribe();

                    this.updateSchemaForecast(localData);
                    return new Promise(resolve => resolve(localData));
                  })
                );
              } else {
                // If not in browser, just store what we have
                this.serverCacheService.storeCityForecastWeatherToServer(localData).pipe(take(1)).subscribe();
                this.updateSchemaForecast(localData);
                return new Promise(resolve => resolve(localData));
              }
            })
          );
        } else {
          // Use cached forecast data
          if (localForecast.weather.forecast && Array.isArray(localForecast.weather.forecast)) {
            localForecast.weather.forecast.forEach(elem => elem.Time = new Date(elem.Time));
            this.data.forecast = localForecast.weather.forecast;
          }

          if (localForecast.weather.forecastYrno && Array.isArray(localForecast.weather.forecastYrno)) {
            localForecast.weather.forecastYrno.forEach(elem => elem.Time = new Date(elem.Time));
            this.data.forecastYrno = localForecast.weather.forecastYrno;
          }

          // Set refreshtime from cached data
          if (localForecast.weather.refreshtime) {
            this.data.refreshtime = new Date(localForecast.weather.refreshtime);
          }

          this.updateSchemaForecast(localForecast);
          return new Promise(resolve => resolve(localForecast));
        }
      })
    ).subscribe({
      next: () => {
        console.log(`Weather data updated for ${this.city}`);
      },
      error: err => {
        console.error('Error updating forecast:', err);
      }
    });
  }

  updateSchemaForecast(data: LocalStorageInformation)
  {

    this.weatherForecastSchema = {
      '@context': 'https://schema.org',
      '@type': 'WeatherForecast',
      'location': {
        '@type': 'Place',
        'name': this.city,

      },
      //'datePublished': data.weather.refreshtime,
      'weatherForecast': []
    };

    // Populate the forecast data dynamically
    for (const forecast of data.weather.forecastYrno) {

      if (forecast.Temperature)
      {
        const forecastObj = {
          '@type': 'Forecast',
          'date': forecast.Time,
          'temperature': forecast.Temperature,
          'windSpeed': {
            '@type': 'QuantitativeValue',
            'value': forecast.WindSpeed,
            'unitCode': 'MPS'
          }
        };
        this.weatherForecastSchema.weatherForecast.push(forecastObj);
    }

    }
    //console.log(JSON.stringify(this.weatherForecastSchema));
    // attach schema to data model so child components (WeatherUI) can access it
    if (!this.data) { this.data = new WeatherData(); }
    this.data.weatherForecastSchema = this.weatherForecastSchema;
  }

}
